import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { startQrScanner, stopQrScanner } from "@/lib/qrscan";
import { roleOf } from "@/lib/roles";
import { Camera, CameraOff, CloudUpload, Search, Printer, WifiOff, CheckCircle2, XCircle, ScanBarcode, UserPlus, Undo2 } from "lucide-react";
import api, { errText } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Badge } from "@/components/Badge";
import { StationBoard } from "@/components/StationBoard";
import { KioskPanel } from "@/components/KioskPanel";
import { useAuth } from "@/context/AuthContext";

const QUEUE_KEY = "ep_offline_queue";
const readQueue = () => JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
const writeQueue = (q) => localStorage.setItem(QUEUE_KEY, JSON.stringify(q));

const chime = (ok, fanfare = false) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const play = (freq, at, dur = 0.25) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.08, ctx.currentTime + at);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + dur);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + at + dur);
    };
    if (fanfare) {
      // Award recipients get a rising three-note alert the crew can't mistake.
      [659, 880, 1175].forEach((f, i) => play(f, i * 0.14, 0.22));
    } else {
      play(ok ? 880 : 220, 0);
    }
  } catch {}
};

const rsvpLabel = (v) => (v === "yes" ? "RSVP yes" : v === "no" ? "RSVP no" : "no reply");

const timeOnly = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
};

const groupByCompany = (rows) => {
  const map = new Map();
  for (const r of rows) {
    const key = (r.company || "").trim() || "No company listed";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  }
  return [...map.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
};

export default function Scanner() {  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const canUndo = ["moderator", "admin"].includes(user?.role);
  const [station, setStation] = useState(localStorage.getItem("ep_station") || "station-1");
  const [scanning, setScanning] = useState(false);
  const [last, setLast] = useState(null);
  const [queue, setQueue] = useState(readQueue());
  const [autoPrint, setAutoPrint] = useState(localStorage.getItem("ep_auto_print") !== "off");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [event, setEvent] = useState(null);
  const [count, setCount] = useState(0);
  const [gun, setGun] = useState(localStorage.getItem("ep_scan_gun") !== "off");
  const [gunValue, setGunValue] = useState("");
  const [walkIn, setWalkIn] = useState({ first_name: "", last_name: "", company: "" });
  const [walkBusy, setWalkBusy] = useState(false);
  const scannerRef = useRef(null);
  const gunRef = useRef(null);
  const busyRef = useRef(false);

  useEffect(() => {
    if (!loading && !user) navigate("/moderator/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    localStorage.setItem("ep_station", station);
  }, [station]);

  useEffect(() => {
    if (!eventId) return;
    api.get(`/public/events`).then(({ data }) => setEvent(data.find((e) => e.event_id === eventId) || null));
  }, [eventId]);

  const handleResult = useCallback(
    (data) => {
      setLast(data);
      const ok = data.result === "success";
      const role = roleOf(data.registration);
      chime(ok, ok && !!role);
      if (ok) {
        setCount((c) => c + 1);
        toast.success(`${data.registration.first_name} ${data.registration.last_name} checked in`);
        if (role)
          toast.warning(
            `${role.label} — ${data.registration.first_name} ${data.registration.last_name}`,
            { duration: 8000 }
          );
        if (autoPrint) setTimeout(() => window.print(), 400);
      } else {
        toast.warning(data.message);
      }
    },
    [autoPrint]
  );

  const submitScan = useCallback(
    async (token) => {
      if (busyRef.current) return;
      busyRef.current = true;
      try {
        const { data } = await api.post("/checkins/scan", {
          qr_token: token,
          event_id: eventId,
          station_id: station,
        });
        handleResult(data);
      } catch (e) {
        if (!navigator.onLine || !e.response) {
          const next = [...readQueue(), { qr_token: token, event_id: eventId, station_id: station, scanned_at: new Date().toISOString() }];
          writeQueue(next);
          setQueue(next);
          toast.info("Offline — scan queued for sync");
        } else {
          toast.error(errText(e));
        }
      } finally {
        setTimeout(() => (busyRef.current = false), 1200);
      }
    },
    [eventId, station, handleResult]
  );

  useEffect(() => {
    localStorage.setItem("ep_scan_gun", gun ? "on" : "off");
  }, [gun]);

  // A USB/Bluetooth scanner types the token then presses Enter, so the field has to keep focus
  // even after a stray click somewhere else on the page.
  useEffect(() => {
    if (!gun) return;
    const claim = () => {
      const active = document.activeElement;
      const typing = active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName);
      if (!typing) gunRef.current?.focus();
    };
    claim();
    const id = setInterval(claim, 1500);
    return () => clearInterval(id);
  }, [gun]);

  const submitGun = (e) => {
    e.preventDefault();
    const code = gunValue.trim();
    setGunValue("");
    if (code) submitScan(code);
  };

  const start = async () => {
    try {
      scannerRef.current = await startQrScanner("qr-reader", (text) => submitScan(text));
      setScanning(true);
    } catch (e) {
      toast.error("Camera unavailable — use the barcode scanner or manual lookup below");
    }
  };

  const stop = async () => {
    await stopQrScanner(scannerRef.current);
    scannerRef.current = null;
    setScanning(false);
  };

  useEffect(() => () => { stopQrScanner(scannerRef.current); }, []);

  const sync = async () => {
    const pending = readQueue();
    if (pending.length === 0) return toast.info("Nothing to sync");
    try {
      const { data } = await api.post("/checkins/sync", { scans: pending });
      writeQueue([]);
      setQueue([]);
      toast.success(`Synced ${data.synced} queued scan(s)`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const search = async (e) => {
    e.preventDefault();
    runSearch(q);
  };

  const runSearch = useCallback(
    async (term) => {
      if (!term.trim()) return setResults([]);
      try {
        const { data } = await api.get(`/events/${eventId}/lookup`, { params: { q: term } });
        setResults(data);
      } catch (err) {
        toast.error(errText(err));
      }
    },
    [eventId]
  );

  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 350);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  const manual = async (r) => {
    if (r.status === "waitlisted" || r.pass_available === false) {
      const why =
        r.status === "waitlisted"
          ? `${r.first_name} ${r.last_name} is on the waitlist`
          : `${r.first_name} ${r.last_name} has no released pass`;
      if (!window.confirm(`${why}. Check them in anyway?`)) return;
    }
    try {
      const { data } = await api.post(`/checkins/manual/${r.registration_id}`, null, { params: { station_id: station } });
      handleResult(data);
      setResults([]);
      setQ("");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const undoCheckIn = async (r) => {
    if (!window.confirm(`Undo the check-in for ${r.first_name} ${r.last_name}?`)) return;
    try {
      const { data } = await api.post(
        `/events/${eventId}/registrations/${r.registration_id}/undo-checkin`
      );
      toast.success(data.message);
      setCount((c) => Math.max(c - 1, 0));
      setLast(null);
      runSearch(q);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const addWalkIn = async (checkInNow) => {
    if (!walkIn.first_name.trim() || !walkIn.last_name.trim())
      return toast.error("First and last name are required");
    setWalkBusy(true);
    try {
      const { data } = await api.post(`/events/${eventId}/walkin`, {
        ...walkIn,
        check_in: checkInNow,
        station_id: station,
      });
      if (data.over_capacity) toast.warning("Heads up — this event is now over capacity");
      if (checkInNow) handleResult(data);
      else toast.success(`${walkIn.first_name} ${walkIn.last_name} added (not checked in)`);
      setWalkIn({ first_name: "", last_name: "", company: "" });
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setWalkBusy(false);
    }
  };

  return (
    <Shell wide>
      <div data-testid="scanner-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="overline">Check-in station</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight leading-none mt-2">
              {event?.name || "Scanner"}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <label className="label">Station ID</label>
              <input className="input w-40" data-testid="station-id-input" value={station} onChange={(e) => setStation(e.target.value)} />
            </div>
            <button onClick={sync} className="btn-secondary" data-testid="sync-queue-btn">
              <CloudUpload size={15} /> Sync ({queue.length})
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_1fr] gap-6 mt-8">
          <div>
            <div className="relative min-h-[300px] rounded-2xl overflow-hidden bg-black border-2 border-[#00e599]/40 shadow-[0_0_30px_rgba(0,229,153,0.15)] grid place-items-center">
              <div id="qr-reader" className="w-full [&_video]:w-full [&_video]:h-auto" />
              {!scanning && (
                <div className="absolute inset-0 grid place-items-center text-center px-6">
                  <div>
                    <Camera className="mx-auto text-[#00e599]" size={34} />
                    <p className="text-sm text-slate-400 mt-3">Camera idle. Start the scanner to check attendees in.</p>
                  </div>
                </div>
              )}
              {scanning && <div className="pointer-events-none absolute left-0 right-0 h-0.5 bg-[#00e599]/70 laser" />}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              {scanning ? (
                <button onClick={stop} className="btn-secondary" data-testid="stop-scan-btn">
                  <CameraOff size={15} /> Stop camera
                </button>
              ) : (
                <button onClick={start} className="btn-primary" data-testid="start-scan-btn">
                  <Camera size={15} /> Start camera
                </button>
              )}
              <label className="flex items-center gap-2 text-xs text-slate-300">
                <input
                  type="checkbox"
                  className="accent-[#00e599] w-4 h-4"
                  data-testid="auto-print-toggle"
                  checked={autoPrint}
                  onChange={(e) => {
                    setAutoPrint(e.target.checked);
                    localStorage.setItem("ep_auto_print", e.target.checked ? "on" : "off");
                  }}
                />
                Auto-print badge on check-in
              </label>
              <span className="font-mono2 text-xs text-slate-500 ml-auto">
                {count} checked in this session
              </span>
            </div>
            {!navigator.onLine && (
              <p className="mt-3 inline-flex items-center gap-2 text-xs text-[#ffb800]">
                <WifiOff size={13} /> Offline — scans are queued locally
              </p>
            )}

            <form onSubmit={submitGun} className="card p-5 mt-6" data-testid="scan-gun-form">
              <div className="flex items-center justify-between gap-3">
                <p className="label mb-0 flex items-center gap-2">
                  <ScanBarcode size={14} className={gun ? "text-[#00e599]" : "text-slate-500"} />
                  Barcode scanner
                </p>
                <label className="flex items-center gap-2 text-xs text-slate-300">
                  <input
                    type="checkbox"
                    className="accent-[#00e599] w-4 h-4"
                    data-testid="scan-gun-toggle"
                    checked={gun}
                    onChange={(e) => setGun(e.target.checked)}
                  />
                  Listening
                </label>
              </div>
              <input
                ref={gunRef}
                className="input mt-3 font-mono2 text-xs"
                placeholder={gun ? "Scan a pass — or type a reference and press Enter" : "Turn listening on to use a scanner"}
                disabled={!gun}
                autoComplete="off"
                data-testid="scan-gun-input"
                value={gunValue}
                onChange={(e) => setGunValue(e.target.value)}
              />
              <p className="text-[11px] text-slate-500 mt-2" data-testid="scan-gun-hint">
                Works with any USB or Bluetooth 2D scanner in keyboard (HID) mode — it types the pass
                and presses Enter. The reference code on the pass works too.
              </p>
            </form>

            <form onSubmit={search} className="card p-5 mt-6" data-testid="manual-lookup-form">
              <p className="label">Manual fallback lookup</p>
              <div className="flex gap-3">
                <input
                  className="input"
                  placeholder="Name, company, email or reference"
                  data-testid="manual-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                <button className="btn-secondary shrink-0" data-testid="manual-search-btn">
                  <Search size={15} /> Find
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Searches as you type. Company names work too — handy when you can't catch a guest's name.
              </p>
              {q.trim() && results.length === 0 && (
                <p className="text-sm text-slate-500 mt-4" data-testid="manual-no-results">
                  No match found.
                </p>
              )}
              {results.length > 0 && (
                <div className="mt-4 space-y-4" data-testid="manual-results">
                  {groupByCompany(results).map(([company, rows]) => (
                    <div key={company}>
                      <p className="font-mono2 text-[11px] uppercase tracking-wider text-[#00e599]">
                        {company} · {rows.length} guest{rows.length === 1 ? "" : "s"}
                      </p>
                      <div className="divide-y divide-[#252f3e]">
                        {rows.map((r) => (
                          <div key={r.registration_id} className="flex items-center justify-between py-2.5 gap-3">
                            <div className="min-w-0">
                              <p className="text-sm truncate">
                                {[r.salutation, r.first_name, r.last_name].filter(Boolean).join(" ")}
                                {r.job_title ? <span className="text-slate-500"> · {r.job_title}</span> : null}
                              </p>
                              <p className="font-mono2 text-[11px] text-slate-500 truncate">
                                REF {r.registration_code} · {rsvpLabel(r.rsvp)}
                                {r.status === "waitlisted" ? " · waitlisted" : ""}
                                {r.status !== "waitlisted" && r.pass_available === false ? " · pass not released" : ""}
                                {r.checked_in ? ` · in ${timeOnly(r.checked_in_at)}` : ""}
                                {r.walk_in ? " · walk-in" : ""}
                                {roleOf(r) ? ` · ${roleOf(r).label}` : ""}
                              </p>
                              <p className="font-mono2 text-[11px] text-slate-600 truncate">{r.email}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                onClick={() => manual(r)}
                                disabled={r.checked_in}
                                className="btn-secondary text-xs py-1.5 px-3 shrink-0"
                                data-testid={`manual-check-${r.registration_code}`}
                              >
                                Check in
                              </button>
                              {r.checked_in && canUndo && (
                                <button
                                  onClick={() => undoCheckIn(r)}
                                  className="btn-ghost text-xs py-1.5 px-2 shrink-0 text-slate-400 hover:text-[#ff6b6b]"
                                  data-testid={`undo-checkin-${r.registration_code}`}
                                >
                                  <Undo2 size={13} /> Undo
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </form>

            <div className="card p-5 mt-6" data-testid="walkin-form">
              <p className="label flex items-center gap-2">
                <UserPlus size={14} className="text-[#00e599]" /> Add a walk-in
              </p>
              <p className="text-[11px] text-slate-500 mt-1">
                For a guest who isn't on the list. No email is sent — they get a reference and a pass
                you can print or email later.
              </p>
              <div className="grid sm:grid-cols-3 gap-3 mt-3">
                <input
                  className="input"
                  placeholder="First name"
                  data-testid="walkin-first-name"
                  value={walkIn.first_name}
                  onChange={(e) => setWalkIn({ ...walkIn, first_name: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="Last name"
                  data-testid="walkin-last-name"
                  value={walkIn.last_name}
                  onChange={(e) => setWalkIn({ ...walkIn, last_name: e.target.value })}
                />
                <input
                  className="input"
                  placeholder="Company (optional)"
                  data-testid="walkin-company"
                  value={walkIn.company}
                  onChange={(e) => setWalkIn({ ...walkIn, company: e.target.value })}
                />
              </div>
              <div className="flex flex-wrap gap-3 mt-3">
                <button
                  onClick={() => addWalkIn(true)}
                  disabled={walkBusy}
                  className="btn-primary"
                  data-testid="walkin-add-checkin-btn"
                >
                  <UserPlus size={15} /> {walkBusy ? "Saving…" : "Add & check in"}
                </button>
                <button
                  onClick={() => addWalkIn(false)}
                  disabled={walkBusy}
                  className="btn-secondary"
                  data-testid="walkin-add-only-btn"
                >
                  Add without checking in
                </button>
              </div>
            </div>
          </div>

          <div>
            <div className="card p-5" data-testid="scan-result-panel">
              <p className="label">Last scan</p>
              {!last ? (
                <p className="text-sm text-slate-500">Waiting for the first scan…</p>
              ) : (
                <div>
                  <p
                    className={`inline-flex items-center gap-2 font-mono2 text-xs px-3 py-1.5 rounded-full border ${
                      last.result === "success"
                        ? "text-[#00e599] border-[#00e599]/40"
                        : "text-[#ffb800] border-[#ffb800]/40"
                    }`}
                    data-testid="scan-result-status"
                  >
                    {last.result === "success" ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                    {last.result} — {last.message}
                  </p>
                  {roleOf(last.registration) && (
                    <div
                      className={`mt-4 rounded-xl border-2 px-4 py-3 ${roleOf(last.registration).panel}`}
                      data-testid="award-alert"
                    >
                      <p className="font-display text-2xl font-black uppercase tracking-wide">
                        {roleOf(last.registration).label}
                      </p>
                      <p className="text-xs opacity-80 mt-0.5">
                        Let the registration lead know they've arrived
                      </p>
                    </div>
                  )}
                  {last.registration && (
                    <p className="text-2xl font-bold mt-4">
                      {last.registration.first_name} {last.registration.last_name}
                    </p>
                  )}
                </div>
              )}
            </div>

            <KioskPanel eventId={eventId} canEdit={["moderator", "admin"].includes(user?.role)} className="mt-5" />

            <StationBoard eventId={eventId} className="mt-5" />

            {last?.registration && event && (
              <div className="mt-5">
                <div className="flex items-center justify-between mb-3">
                  <p className="label mb-0">Badge</p>
                  <button onClick={() => window.print()} className="btn-ghost text-xs" data-testid="scanner-print-btn">
                    <Printer size={13} /> Print
                  </button>
                </div>
                <Badge registration={last.registration} event={event} />
              </div>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}
